import React, { useState, useReducer, createRef, useEffect } from 'react'

import Popup from '../../partials/Popup/Popup';
import ConfirmationPopup from '../../partials/Popup/ConfirmationPopup';
import './ConflictResolution.scss'

function ConflictConnectorArrow(props) {
  if (!props.dimensions) {
    return null;
  }
  const HEIGHT = 37;
  const WIDTH = props.count * props.dimensions.width;
  // TODO better express the pairs of conflicting areas
  const areaPairs = [];
  for (let i = 0; i < props.indices.length - 1; i++) {
    for (let j = i + 1; j < props.indices.length; j++) {
      areaPairs.push([props.indices[i], props.indices[j]])
    }
  }
  const getAnchorPoint = (i) => [props.dimensions.width * (i + 0.5), HEIGHT];
  return <svg height={HEIGHT + 5} width={WIDTH}>
    {areaPairs.map(pair => {
      const [x1, y1] = getAnchorPoint(pair[0]);
      const [x2, y2] = getAnchorPoint(pair[1]);
      return <polyline points={
        `${x1},${0} ${x1},${20} ${x1 + 20},${y1} ${x2 - 20},${y2} ${x2},${20} ${x2},${0}`
      } stroke="red" stroke-width="2px" fill="none" />
    })}</svg>;
}

function AreasSelector(props) {
  const conflictIndices = Object.keys(props.foundConflicts).map(
    k => props.areas.findIndex(a => a.id === k));
  const containerRef = createRef();
  const [arrowLayout, setArrowLayout] = useState(null);
  useEffect(() => {
    if (arrowLayout) {
      return;
    }
    const { width, height } = containerRef.current.firstChild.getBoundingClientRect();
    setArrowLayout({ width, height });
  }, [containerRef, arrowLayout]);

  return <>
    <ul className="AreasSelector" ref={containerRef}>
      {props.areas.map((area, i) => {
        const className = [
          area.id in props.foundConflicts ? "conflict" : "",
          area.id === props.selectedArea ? "selected" : ""
        ].join(" ");
        return <li
          key={area.id}
          onClick={() => props.onChange(area.id)}>
          <AreaIcon className={className} conflictBadge={[]} conflictMessage={props.foundConflicts[area.id]}>
            <img src={area.display.icon} alt={area.display.label + " icon"} />
            <p>{area.display.label}</p>
          </AreaIcon>
        </li>;
      })}
    </ul>
    <ConflictConnectorArrow indices={conflictIndices} count={props.areas.length} dimensions={arrowLayout} />
  </>;
}

function AreaIcon(props) {
  const className = `AreaCard ${props.className || ''}`;
  const [popupOpen, setPopupOpen] = useState(false);

  const handleOnClose = () => setPopupOpen(false);

  const popupData = props.conflictMessage && {
    header: "Comments on the change you just made",
    // popup requires html here
    body: props.conflictMessage.map(txt => `<p>${txt}</p>`).join(''),
    button: "Continue"
  }

  return <>
    <div className={className} onClick={props.onClick}>
      {props.children}
      {props.conflictBadge && props.conflictBadge.map(b =>
        <div className="conflictBadge">{b}</div>)}
      {props.conflictMessage &&
        <div className="infoButton" onClick={() => setPopupOpen(true)}>?</div>}
    </div>
    {props.conflictMessage && popupOpen && <Popup scrim={true} data={popupData} onClose={handleOnClose} />}
  </>
}

function ChoiceButton(props) {
  const className = `ChoiceButton ${props.className}`;
  return <div className={className}>
    <button onClick={props.onClick}>{props.text}</button>
  </div>
}

function TabContent(props) {
  const [popupData, setPopupData] = useState(null);

  const closePopup = () => {
    setPopupData(null);
  }

  const handleOnClose = () => {
    closePopup();
  };
  const handleOnComplete = () => {
    const {optionId} = popupData;
    closePopup();
    props.setOptionValue(optionId);
  };

  const choiceOnClick = (optionId) => {
    const targetOptionText = props.area.options.find(o => o.id === optionId).text;
    setPopupData({
      header: 'Make a change',
      body: `Change to “${targetOptionText}”`,
      buttonCancel: "cancel",
      buttonConfirm: "confirm",
      optionId: optionId
    });
  };

  return <div>
    <h2 style={{color: "#ABA4A7", fontSize: "16px", fontStyle: "italic"}}>Make a change:</h2>
    <div className="Choices">
      <h2 className="choice-header">{props.area.display.label}</h2>
      {props.area.options.map(o => {
        const className = props.selectedOption === o.id ? "selected" : "";
        return <ChoiceButton className={className} key={o.id}
          id={o.id} text={o.text} onClick={() => choiceOnClick(o.id)} />;
      })}
    </div>
    {popupData && <ConfirmationPopup scrim={true} instant={true} data={popupData}
      onClose={handleOnClose} onComplete={handleOnComplete} />}
  </div>;
}

function findConflicts(conflicts, selections, areas) {
  const optionToArea = optionId => areas.find(
    a => a.options.some(o => o.id === optionId)).id;
  const selectedOptions = Object.values(selections);
  const foundConflicts = Object.create(null);
  for (const c of conflicts) {
    if (c.options.every(o => selectedOptions.includes(o))) {
      c.options.forEach(o => {
        const areaId = optionToArea(o);
        (foundConflicts[areaId] = foundConflicts[areaId] || []).push(c.message);
      });
    }
  }
  return foundConflicts;
}

function parseOptions(areas) {
  // extract the areas and initial options into the initial state
  const selections = {};
  for (const a of areas) {
    selections[a.id] = a.initialOption;
  }
  return selections;
}

const ConflictResolution = props => {
  const [selections, setSelection] = useReducer(
    (state, { area, value }) => {
      state = { ...state };
      state[area] = value;
      return state;
    },
    parseOptions(props.data.areas),
  )
  const [selectedArea, setSelectedArea] = useState(props.data.areas[0].id);
  const [introOpen, setIntroOpen] = useState(true);

  const selectedAreaIndex = props.data.areas.findIndex(a => a.id === selectedArea);
  const foundConflicts = findConflicts(props.data.conflicts, selections, props.data.areas);

  return <div className="ContentResolution">
    <AreasSelector
      areas={props.data.areas}
      selectedArea={selectedArea}
      foundConflicts={foundConflicts}
      onChange={areaId => setSelectedArea(areaId)} />
    <TabContent
      area={props.data.areas[selectedAreaIndex]}
      selectedOption={selections[selectedArea]}
      setOptionValue={value => setSelection({ area: selectedArea, value })}
    />
    {(Object.entries(foundConflicts).length === 0) && <div className="AllClear">
      <button className="global-button" onClick={() => props.onComplete()}>All clear</button>
    </div>}
    {introOpen && <div style={{zIndex:"10"}}><Popup data={props.data.activitySetup} onClose={() => setIntroOpen(false)} /></div>}
  </div>
}

export default ConflictResolution
